import { formatUnknownValue } from 'src/modules/shared/utils/formatting/unknown-value.util';
import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import SchemaPathPicker from './schema-path-picker';
import LiteralJsonEditor from './literal-json-editor';
import InterpolatedStringEditor from './interpolated-string-editor';
import {
  type ActionInputFieldDef,
  type InputMappingMode,
  parseWorkflowInputTemplate,
  parseStepOutputTemplate,
  isMultilineStringField,
} from './step-inputs-mapping-utils';
import { isPlainObject } from './workflow-schema-utils';
import { type ReferenceOption, type StepOutputOption } from './types';

/** Props contract. */
interface Props {
  /** Identifier of the step being edited. */
  stepId: string;
  /** Definition of the action input field. */
  field: ActionInputFieldDef;
  /** Selected source for the input value. */
  mode: InputMappingMode;
  /** Current input value or mapping expression. */
  value: unknown;
  /** Workflow input names available for references. */
  workflowInputKeys: string[];
  /** Schema used to describe available workflow input values. */
  workflowInputSchema?: Record<string, unknown>;
  /** Available output values from other steps. */
  stepOutputs: StepOutputOption[];
  /** Selectable references for input mapping. */
  referenceOptions: ReferenceOption[];
  /** Changes the source used for the input value. */
  onModeChange: (key: string, mode: InputMappingMode) => void;
  /** Removes the current input mapping. */
  onClear: (key: string) => void;
  /** Updates the current input value. */
  onChangeValue: (key: string, value: unknown) => void;
}

/** Renders a string literal without mixing it with the other input-kind branches. */
const renderStringLiteral = (
  field: ActionInputFieldDef,
  value: unknown,
  updateFieldValue: (key: string, value: unknown) => void
) => {
  const str =
    typeof value === 'string'
      ? value
      : value === undefined || value === null
        ? ''
        : formatUnknownValue(value);
  const multiline = isMultilineStringField(field.key) || str.includes('\n');

  const handleChange = (raw: string) => {
    if (raw === '') {
      updateFieldValue(field.key, field.required ? '' : undefined);
      return;
    }
    updateFieldValue(field.key, raw);
  };

  return multiline ? (
    <textarea
      className="border rounded px-2 py-1.5 text-sm w-full min-h-[96px] resize-y"
      value={str}
      onChange={(e) => handleChange(e.target.value)}
      placeholder="Escribe aquí…"
    />
  ) : (
    <input
      className="border rounded px-2 py-1.5 text-sm w-full"
      value={str}
      onChange={(e) => handleChange(e.target.value)}
      placeholder="Escribe aquí…"
    />
  );
};

const renderLiteralEditor = (
  field: ActionInputFieldDef,
  value: unknown,
  updateFieldValue: (key: string, value: unknown) => void
) => {
  if (field.type === 'boolean') {
    return (
      <select
        className="border rounded px-2 py-1.5 text-sm w-full"
        value={typeof value === 'boolean' ? formatUnknownValue(value) : ''}
        onChange={(e) => {
          if (e.target.value === '') {
            updateFieldValue(field.key, undefined);
            return;
          }
          updateFieldValue(field.key, e.target.value === 'true');
        }}
      >
        <option value="">Sin valor</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  if (field.type === 'number' || field.type === 'integer') {
    return (
      <input
        type="number"
        className="border rounded px-2 py-1.5 text-sm w-full"
        value={typeof value === 'number' ? formatUnknownValue(value) : ''}
        onChange={(e) => {
          if (e.target.value === '') {
            updateFieldValue(field.key, undefined);
            return;
          }
          const parsed =
            field.type === 'integer'
              ? Number.parseInt(e.target.value, 10)
              : Number(e.target.value);
          updateFieldValue(
            field.key,
            Number.isNaN(parsed) ? undefined : parsed
          );
        }}
      />
    );
  }

  if (
    field.type === 'string' &&
    field.enumValues &&
    field.enumValues.length > 0
  ) {
    return (
      <select
        className="border rounded px-2 py-1.5 text-sm w-full"
        value={
          typeof value === 'string' || typeof value === 'number'
            ? formatUnknownValue(value)
            : ''
        }
        onChange={(e) => {
          if (e.target.value === '') {
            updateFieldValue(field.key, undefined);
            return;
          }
          updateFieldValue(field.key, e.target.value);
        }}
      >
        <option value="">Selecciona…</option>
        {field.enumValues.map((ev) => (
          <option key={`${field.key}-${String(ev)}`} value={String(ev)}>
            {String(ev)}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'string') {
    return renderStringLiteral(field, value, updateFieldValue);
  }

  if (field.type === 'object' || field.type === 'array') {
    return (
      <LiteralJsonEditor
        fieldKey={field.key}
        value={value}
        schema={isPlainObject(field.schema) ? field.schema : undefined}
        formOnly
        forceValueType={field.type}
        onChange={(next) => updateFieldValue(field.key, next)}
      />
    );
  }

  return (
    <LiteralJsonEditor
      fieldKey={field.key}
      value={value}
      onChange={(next) => updateFieldValue(field.key, next)}
    />
  );
};

const WorkflowInputEditor: React.FC<{
  field: ActionInputFieldDef;
  value: unknown;
  workflowInputKeys: string[];
  workflowInputSchema?: Record<string, unknown>;
  updateFieldValue: (key: string, value: unknown) => void;
}> = ({
  field,
  value,
  workflowInputKeys,
  workflowInputSchema,
  updateFieldValue,
}) => {
  const currentPath = parseWorkflowInputTemplate(value) ?? '';

  if (workflowInputKeys.length === 0) {
    return (
      <Text size="tiny" className="text-slate-500">
        No hay entradas del flujo definidas.
      </Text>
    );
  }

  return (
    <SchemaPathPicker
      schemaRoot={workflowInputSchema}
      options={workflowInputKeys}
      valuePath={currentPath}
      onChangePath={(nextPath) => {
        if (!nextPath) {
          updateFieldValue(field.key, undefined);
          return;
        }
        updateFieldValue(field.key, `{{inputs.${nextPath}}}`);
      }}
      helperPrefix="inputs"
    />
  );
};

const StepOutputEditor: React.FC<{
  field: ActionInputFieldDef;
  stepOutputs: StepOutputOption[];
  value: unknown;
  updateFieldValue: (key: string, value: unknown) => void;
}> = ({ field, stepOutputs, value, updateFieldValue }) => {
  const parsed = parseStepOutputTemplate(value);
  const selectedStep = parsed?.stepId ?? '';
  const selectedKeyPath = parsed?.key ?? '';
  const availableSteps = stepOutputs;
  const activeStep =
    availableSteps.find((s) => s.stepId === selectedStep) ??
    availableSteps.at(0);
  const outputKeys = activeStep?.outputKeys ?? [];
  const activeStepId = activeStep?.stepId ?? '';
  const outputSchemaRoot = activeStep?.outputSchema;

  if (availableSteps.length === 0) {
    return (
      <Text size="tiny" className="text-slate-500">
        No hay pasos anteriores disponibles.
      </Text>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={`${field.key}-step`} className="text-xs text-slate-600">
          Paso
        </label>
        <select
          id={`${field.key}-step`}
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={activeStepId}
          onChange={(e) => {
            const nextStep = e.target.value;
            const nextOutputs =
              availableSteps.find((s) => s.stepId === nextStep)?.outputKeys ??
              [];
            const nextKey = nextOutputs[0] || '';
            if (!nextKey) {
              updateFieldValue(field.key, undefined);
            } else {
              updateFieldValue(
                field.key,
                `{{steps.${nextStep}.outputs.${nextKey}}}`
              );
            }
          }}
        >
          {availableSteps.map((s) => (
            <option key={`${field.key}-step-${s.stepId}`} value={s.stepId}>
              {s.stepId} · {s.actionId}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <Text size="tiny" color="secondary">
          Salida
        </Text>
        {outputKeys.length === 0 ? (
          <Text size="tiny" className="text-slate-500">
            No se pudieron inferir salidas para este paso.
          </Text>
        ) : (
          <SchemaPathPicker
            schemaRoot={
              outputSchemaRoot && isPlainObject(outputSchemaRoot)
                ? outputSchemaRoot
                : undefined
            }
            options={outputKeys}
            valuePath={selectedKeyPath}
            onChangePath={(nextPath) => {
              if (!nextPath) {
                updateFieldValue(field.key, undefined);
                return;
              }
              updateFieldValue(
                field.key,
                `{{steps.${activeStepId}.outputs.${nextPath}}}`
              );
            }}
            helperPrefix={`steps.${activeStepId}.outputs`}
          />
        )}
      </div>
    </div>
  );
};

export const StepInputFieldCard: React.FC<Props> = ({
  stepId,
  field,
  mode,
  value,
  workflowInputKeys,
  workflowInputSchema,
  stepOutputs,
  referenceOptions,
  onModeChange,
  onClear,
  onChangeValue,
}) => {
  const updateFieldValue = (key: string, val: unknown) =>
    onChangeValue(key, val);

  const renderWorkflowInputEditor = () => (
    <WorkflowInputEditor
      field={field}
      value={value}
      workflowInputKeys={workflowInputKeys}
      workflowInputSchema={workflowInputSchema}
      updateFieldValue={updateFieldValue}
    />
  );

  const renderStepOutputEditor = () => (
    <StepOutputEditor
      field={field}
      stepOutputs={stepOutputs}
      value={value}
      updateFieldValue={updateFieldValue}
    />
  );

  const renderInterpolatedEditor = () => {
    const str =
      typeof value === 'string'
        ? value
        : value === undefined || value === null
          ? ''
          : formatUnknownValue(value);
    return (
      <div className="space-y-1">
        <InterpolatedStringEditor
          fieldKey={field.key}
          value={str}
          options={referenceOptions}
          onChange={(next) => updateFieldValue(field.key, next)}
        />
        <Text size="tiny" className="text-slate-500">
          Usa <span className="font-mono">/</span> para insertar referencias
        </Text>
      </div>
    );
  };

  return (
    <div
      key={`${stepId}-${field.key}`}
      className="border border-slate-200 rounded-lg bg-white p-3 space-y-3"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Text
              size="small"
              weight="medium"
              className="text-slate-800 break-all"
            >
              {field.key}
            </Text>
            {field.required && (
              <span className="text-xs text-rose-600">(requerido)</span>
            )}
            <span className="text-xs text-slate-500">· {field.type}</span>
          </div>
          {field.description && (
            <Text size="tiny" className="text-slate-500">
              {field.description}
            </Text>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="border rounded px-2 py-1 text-sm"
            value={mode}
            onChange={(e) =>
              onModeChange(field.key, e.target.value as InputMappingMode)
            }
          >
            <option value="literal">Literal / JSON</option>
            <option value="workflow-input">Entrada del flujo</option>
            <option value="step-output">Salida de paso</option>
            <option value="interpolated">Texto con referencias</option>
          </select>
          <Button
            type="button"
            variant="neutral"
            appearance="ghost"
            size="sm"
            onClick={() => onClear(field.key)}
          >
            Limpiar
          </Button>
        </div>
      </div>

      <div>
        {mode === 'literal'
          ? renderLiteralEditor(field, value, updateFieldValue)
          : mode === 'workflow-input'
            ? renderWorkflowInputEditor()
            : mode === 'step-output'
              ? renderStepOutputEditor()
              : renderInterpolatedEditor()}
      </div>
    </div>
  );
};

export default StepInputFieldCard;
